import React, { useEffect, useState } from "react";
import Select from "../../../form/select/Select";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import { postAddEdit, postDetails } from "../../../../services/postService";
import { useMutation, useQuery } from "@tanstack/react-query";
import Input from "../../../form/Input";
import { toast } from "react-toastify";
import { taxonomyList } from "../../../../services/taxonomyService";
import Editor from "../../../elements/tiptap-editor/Editor";
import DatePicker from "../../../form/DatePicker";
import Textarea from "../../../form/Textarea";
import MultiselectDropdown from "../../../form/MultiselectDropdown";
import Tags from "../../../form/Tags";
import { useNavigate, useParams } from "react-router-dom";
import SelectedMediaCard from "../../../elements/SelectedMediaCard";
import SelectImageGallery from "../../../form/SelectImageGallery";
import SelectSingleImage from "../../../form/SelectSingleImage";
import FlexibleSelect from "../../../form/select/FlexibleSelect";
import LanguageTabs from "../../../elements/LanguageSwitcher";
import { generateSlug, supportedLanguages, userLang } from "../../../../helpers";
import { userList } from "../../../../services/userService";

const validation = {
  title: { required: true, message: "Title is required" },
};

const ManagePhoto = ({ getResponse = () => {}, close = () => {} }) => {
  const params = useParams();
  const navigate = useNavigate();
  const id = params?.id;
  const [tags, setTags] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [coverMedia, setCoverMedia] = useState("");
  const { values, handleChange, handleSubmit, errors, setFieldsValue, resetField, formValidate } = useForm({
    validationRules: validation,
    initialState: { status: "active" },
  });
  const [categories, setCategories] = useState([]);
  const [artist, setArtist] = useState();
  const [currentLang, setCurrentLang] = useState(localStorage.getItem("lang") || "en");

  const handleLangChange = (lang) => {
    setCurrentLang(lang);
  };

  const { data: details } = useQuery({
    queryKey: ["post-details", id],
    queryFn: async () => {
      const res = await postDetails({ id: id });
      return res?.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (details?._id) {
      setFieldsValue({
        ...details,
        publishStartDate: details?.schedule?.startDate,
        publishEndDate: details?.schedule?.endDate,
        content: {
          ...supportedLanguages.reduce((acc, lang) => {
            acc[lang.code] = {
              title: details?.content?.[lang.code]?.title || "",
              content: details?.content?.[lang.code]?.content || "",
              excerpt: details?.content?.[lang.code]?.excerpt || "",
              seo: details?.content?.[lang.code]?.seo || {},
            };
            return acc;
          }, {}),
        },
        title: details?.content?.[currentLang]?.title || "",
      });
      setCategories(details?.categories);
      setArtist(details?.artistDetails?._id);
      setCoverMedia(details?.coverMedia);
      setSelectedMedia(details?.mediaGallery);
      setTags(
        details.tagDetails.map((item) => ({
          label: item?.content?.[currentLang]?.title,
          _id: item._id,
        }))
      );
    }
  }, [details]);

  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      // const media = selectedMedia?.reduce((acc, item, index) => {
      //   acc[`mediaGallery[${index}]`] = item?._id;
      //   return acc;
      // }, {});

      const media =
        selectedMedia?.length > 0
          ? selectedMedia.reduce((acc, item, index) => {
              if (item?._id) acc[`mediaGallery[${index}]`] = item._id;
              return acc;
            }, {})
          : { mediaGallery: "null" };

      const tag = tags?.reduce((acc, tag, index) => {
        acc[`tags[${index}]`] = tag;
        return acc;
      }, {});

      // const categories = val?.categories?.reduce((acc, category, index) => {
      //   acc[`categories[${index}]`] = category;
      //   return acc;
      // }, {});

      return await postAddEdit({
        content: val?.content,
        slug: `${generateSlug(val?.content?.[userLang]?.title.replace(/\s+/g, "-").toLowerCase(), Date.now())}`,
        lang: localStorage.getItem("lang"),
        status: val?.status,
        publishStartDate: val?.publishStartDate,
        publishEndDate: val?.publishEndDate,
        points: val?.points,
        type: "post-photo",
        coverMedia: coverMedia?._id ? coverMedia?._id : "null",
        categories: categories,
        user: artist,
        ...(details?._id && { id: details._id }),
        ...media,
        ...tag,
        // ...categories,
      });
    },

    onSuccess: (res) => {
      getResponse(res);
      if (res?.status === 200 || res?.status === 201) {
        navigate(`/admin/photos`);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });
  const { data: categoryData } = useQuery({
    queryKey: ["category"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "post-photo-category",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: userData } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const res = await userList({
        limit: 10000,
        role: "artist",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: tagsData } = useQuery({
    queryKey: ["tags"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "post-photo-tag",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  return (
    <>
      <form onSubmit={(event) => handleSubmit(event, mutate)}>
        <div className="flex justify-between items-center w-full px-6">
          <div className="font-normal text-lg xl:text-3xl text-primary">Manage Photos</div>
          <LanguageTabs languages={supportedLanguages} defaultLang="en" value={currentLang} onChange={handleLangChange} />
          <div className="flex gap-4">
            <Button
              buttonType="submit"
              buttonLabel="Save Photo"
              buttonClasses="min-w-28"
              buttonLabelClasses="mx-auto"
              {...(isLoading && {
                loading: true,
                buttonIconPosition: "left",
                isDisable: true,
              })}
            />
            <Button
              buttonLabel="Cancel"
              buttonClasses="min-w-28 !bg-white !text-primary border !border-primary"
              buttonLabelClasses="mx-auto"
              buttonHasLink={true}
              buttonLink={"/admin/photos"}
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-6 p-6">
          <div className="col-span-3 space-y-4">
            <Input
              label="Title"
              inputPlaceholder="Enter title"
              onChange={(e) => {
                const newValue = e.target.value;
                setFieldsValue({
                  ...values,
                  title: newValue,
                  content: {
                    ...values.content,
                    [currentLang]: {
                      ...values.content?.[currentLang],
                      title: newValue,
                    },
                  },
                });
              }}
              inputName={"title"}
              required={false}
              value={values?.content?.[currentLang]?.title || ""}
              {...formValidate(errors, "title")}
            />

            <Editor
              label={"Content"}
              height={200}
              value={values?.content?.[currentLang]?.content || ""}
              placeholder={"Write Something"}
              onChange={(val) => {
                setFieldsValue({
                  ...values,
                  content: {
                    ...values.content,
                    [currentLang]: {
                      ...values.content?.[currentLang],
                      content: val,
                    },
                  },
                });
                // validate this field immediately
                // formValidate(errors, `content.${currentLang}.content`, newValue);
              }}
              // {...formValidate(errors, `content.${currentLang}.content`)}
            />

            <Textarea
              label="Excerpt"
              inputPlaceholder="Write excerpt"
              inputClasses="!h-28"
              inputName="excerpt"
              inputValue={values?.content?.[currentLang]?.excerpt || ""}
              // onChange={handleChange}
              onChange={(e) => {
                const newValue = e.target.value;
                setFieldsValue({
                  ...values,
                  content: {
                    ...values.content,
                    [currentLang]: {
                      ...values.content?.[currentLang],
                      excerpt: newValue,
                    },
                  },
                });
                // formValidate(errors, `content.${currentLang}.title`, newValue);
              }}
            />
            <div className="bg-white p-4 rounded-md border border-text-secondary/50">
              <SelectImageGallery label="Choose Media" values={selectedMedia} onChange={(newMedia) => setSelectedMedia(newMedia)} isSingle={false} />
            </div>

            <div className="relative col-span-4 mt-5">
              <div className="flex gap-4">
                <Button
                  buttonType="submit"
                  buttonLabel={"Save Photo"}
                  buttonClasses={"min-w-28"}
                  buttonLabelClasses={"mx-auto"}
                  {...(isLoading && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
                <Button
                  buttonLabel={"Cancel"}
                  buttonClasses={"min-w-28 !bg-white !text-primary border !border-primary"}
                  buttonLabelClasses={"mx-auto"}
                  buttonHasLink={true}
                  buttonLink={"/admin/photos"}
                />
              </div>
            </div>
          </div>

          <div className="col-span-1 space-y-6">
            <div className="border border-gray-300 rounded-lg p-4 space-y-2">
              <SelectSingleImage
                label="Upload Cover Media"
                size="lg"
                value={coverMedia}
                onChange={(img) => setCoverMedia(img)}
                onRemove={() => setCoverMedia(null)}
              />
            </div>

            <div className="border border-gray-300 rounded-lg p-4 space-y-2">
              <DatePicker
                label={"Publish Start Date"}
                placeholder={"Start date"}
                position="fixed"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "publishStartDate",
                      value: e?.startDate || e?.endDate,
                    },
                  })
                }
                value={{
                  startDate: values?.publishStartDate,
                  endDate: values?.publishStartDate,
                }}
                // {...formValidate(errors, "date")}
              />
              <DatePicker
                label={"Publish End Date"}
                placeholder={"End date"}
                position="fixed"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "publishEndDate",
                      value: e?.startDate || e?.endDate,
                    },
                  })
                }
                value={{
                  startDate: values?.publishEndDate,
                  endDate: values?.publishEndDate,
                }}
                // {...formValidate(errors, "date")}
              />
              <FlexibleSelect
                label={"Status"}
                placeholder={"Choose status"}
                onChange={(e) => handleChange({ target: { name: "status", value: e } })}
                selectedValue={values?.status}
                dropdownData={[
                  { name: "Active", value: "active" },
                  { name: "Inactive", value: "inactive" },
                  { name: "Draft", value: "draft" },
                  { name: "Private", value: "private" },
                ]}
                {...formValidate(errors, "status")}
              />
              {/* <LanguageTabs languages={supportedLanguages} defaultLang="en" value={currentLang} onChange={handleLangChange} /> */}
              <FlexibleSelect
                label={"Select category"}
                isSearch={true}
                dropdownData={categoryData?.data?.map((item) => {
                  return {
                    name: item?.content?.[userLang]?.title,
                    value: item?._id,
                  };
                })}
                selectedValue={categories}
                onChange={setCategories}
                type="multiple"
                placeholder="Choose categories"
              />
              <FlexibleSelect
                label={"Select Artist"}
                isSearch={true}
                dropdownData={userData?.data?.map((item) => {
                  return {
                    name: `${item?.firstName} ${item?.lastName}`,
                    value: item?._id,
                  };
                })}
                selectedValue={artist}
                onChange={setArtist}
                type="single"
                placeholder={"Pick Artist"}
              />
              <Input
                label={"Reward Points"}
                inputPlaceholder="Add Points"
                inputName={"points"}
                value={values?.points}
                onChange={handleChange}
                //   {...formValidate(errors, "lastName")}
              />
              <Tags
                label="Tags"
                placeholder="Enter tags"
                options={tagsData?.data?.map((item) => ({
                  label: item.content?.[userLang]?.title,
                  _id: item._id,
                }))}
                selectedItems={tags}
                setSelectedItems={setTags}
                tagsType="input"
              />
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default ManagePhoto;
